import flagFootballCustomFormData from "../sports/flag-football/config/custom-form-data";
import footballCustomFormData from "../sports/football/config/custom-form-data";
import tennisCustomFormData from "../sports/tennis/config/custom-form-data";
import PlayerPosition from "../sports/football/enums/player-position";
import SportCodes from "../sports/common/sport-codes";
import { DefaultTennisConfig } from "../sports/tennis/config/default";
import { PlayerYears } from "../sports/common/enums/player-years";
import DefaultFlagFootballConfig from "../sports/flag-football/config/default";
import DefaultFootballConfig from "../sports/football/config/default";
import { areEqual, deepCopy } from "./common/object-util";
import DefaultCflConfig from "../sports/football/config/default-cfl";
import cflCustomFormData from "../sports/football/config/cfl-custom-form-data";
import DefaultIndoorConfig from "../sports/football/config/default-indoor";
import indoorCustomFormData from "../sports/football/config/indoor-custom-form-data";
import { fromCamelCase } from "./common/sport-util";
import FieldAreaType from "../sports/football/enums/field-area-type";
import YardConverter from "../sports/common/utils/yard-converter";
import FootballRuleset from "../sports/football/enums/ruleset";
import TennisRuleset from "../sports/tennis/enums/ruleset";

// Only the marker fields store yard positions; every other numeric setting is
// a plain number and must not be rendered as an H/V yard.
const POSITIONAL_FIELDS = ["firstDownYardSet", "fieldAreaSet"];

const sportRuleSetMap = {
    [SportCodes.Football]: {
        [FootballRuleset.DefaultFootball.code]: {
            config: DefaultFootballConfig,
            formData: footballCustomFormData,
            name: FootballRuleset.DefaultFootball.name
        },
        [FootballRuleset.Cfl.code]: {
            config: DefaultCflConfig,
            formData: cflCustomFormData,
            name: FootballRuleset.Cfl.name
        },
        [FootballRuleset.IndoorFootball.code]: {
            config: DefaultIndoorConfig,
            formData: indoorCustomFormData,
            name: FootballRuleset.IndoorFootball.name
        }
    },
    [SportCodes.FlagFootball]: {
        [FootballRuleset.DefaultFlagFootball.code]: {
            config: DefaultFlagFootballConfig,
            formData: flagFootballCustomFormData,
            name: FootballRuleset.DefaultFlagFootball.name
        }
    },
    [SportCodes.Tennis]: {
        [TennisRuleset.DefaultTennis.code]: {
            config: DefaultTennisConfig,
            formData: tennisCustomFormData,
            name: TennisRuleset.DefaultTennis.name
        }
    },
    [SportCodes.WomenTennis]: {
        [TennisRuleset.DefaultTennis.code]: {
            config: DefaultTennisConfig,
            formData: tennisCustomFormData,
            name: TennisRuleset.DefaultTennis.name
        }
    }
};

const defaultRuleSetIdMap = {
    [SportCodes.Football]: FootballRuleset.DefaultFootball.code,
    [SportCodes.FlagFootball]: FootballRuleset.DefaultFlagFootball.code,
    [SportCodes.WomenTennis]: TennisRuleset.DefaultTennis.code,
    [SportCodes.Tennis]: TennisRuleset.DefaultTennis.code
};

const footballPositions = Object.values(PlayerPosition).sort((a, b) => {
    return a.code.localeCompare(b.code);
});

const playerPositions = {
    [SportCodes.FlagFootball]: footballPositions,
    [SportCodes.Football]: footballPositions
};

class SportService {
    resolveDefaultConfig(sportId, ruleSetId) {
        return deepCopy(sportRuleSetMap[sportId]?.[ruleSetId]?.config) || {};
    }

    resolveCustomFormData(sportId, ruleSetId) {
        return deepCopy(sportRuleSetMap[sportId]?.[ruleSetId]?.formData) || {};
    }

    /**
     * The one-sentence definitions for every setting an event's form renders,
     * scoped to its ruleset and ordered as the form orders them.
     *
     * Built from the resolved configuration rather than the ruleset defaults,
     * so the list matches what the crew is actually looking at. A key the
     * config carries that this ruleset has no form data for (the season and
     * event merges replace whole sections, so an Indoor-saved config carries
     * indoor scoring keys into every football ruleset) has no definition and
     * is left out rather than invented.
     *
     * Ordering and headings mirror Configuration.buildUiSchema in
     * prestostats-ui, which is the only thing that decides what the crew
     * sees. Two details of it are easy to get wrong, so both are covered by
     * tests/services/sport-service-setting-definitions.test.js:
     *
     * - buildUiSchema never applies form data to a section, so the
     *   section-level `title` and `order` keys these files carry are dead
     *   there. Sections render in config key order under fromCamelCase names,
     *   and reading those keys here instead printed flag football's sections
     *   in the wrong order under names its form never shows.
     * - every field is seeded with row 1 / order 1 before its form data is
     *   applied over it, so a field that sets neither sorts as 1 rather than
     *   sorting as absent.
     *
     * @param {string} sportId
     * @param {string} ruleSetId
     * @param {object} gameConfiguration resolved event or season configuration
     *
     * Grouped by section, because that is the shape every consumer needs and
     * the shape this already builds internally: returning it flat would mean
     * repeating the section title on every entry and asking the caller to
     * rebuild the grouping. Sections with no defined settings are omitted.
     *
     * @returns {{section: string, sectionTitle: string,
     *            entries: {key: string, title: string,
     *                      description: string}[]}[]}
     */
    getSettingDefinitions(sportId, ruleSetId, gameConfiguration = {}) {
        const customFormData = this.resolveCustomFormData(sportId, ruleSetId);
        // A default only covers undefined, and callers legitimately hold a
        // null configuration while an event or season is still loading.
        const config = gameConfiguration || {};
        const formatPosition = this._positionFormatter(config);

        // Every defined setting, tagged with the section that DRAWS it. That
        // is its config section unless the form data names another one - a
        // presentational regrouping, so the config key itself never moves.
        const entries = [];
        Object.keys(config)
            .filter((section) => customFormData[section])
            .forEach((section) => {
                Object.keys(config[section] || {})
                    .filter((key) => key !== "_type")
                    .forEach((key) => {
                        const formData = customFormData[section][key];
                        // A setting shown only under some values of another
                        // (visibleWhen) is still defined: the sheet explains
                        // every mode, not just the one saved.
                        if (
                            !formData ||
                            !formData.description ||
                            formData.disabled
                        ) {
                            return;
                        }

                        entries.push({
                            section: formData.section || section,
                            key: `${section}.${key}`,
                            configKey: key,
                            formData,
                            value: this._formatSettingValue(
                                config[section][key],
                                formData,
                                key,
                                formatPosition
                            )
                        });
                    });
            });

        // A section the form data declares outranks first-appearance order,
        // which is all the config can offer and which a regrouped setting
        // would otherwise decide by accident.
        const appeared = [];
        entries.forEach(({ section }) => {
            if (!appeared.includes(section)) appeared.push(section);
        });
        const sectionOrder = customFormData.sectionOrder
            ? customFormData.sectionOrder.filter((s) => appeared.includes(s))
            : appeared;

        return sectionOrder
            .map((section) => ({
                section,
                sectionTitle: fromCamelCase(section),
                entries: entries
                    .filter((entry) => entry.section === section)
                    .sort(
                        (a, b) =>
                            (a.formData.row ?? 1) - (b.formData.row ?? 1) ||
                            (a.formData.order ?? 1) - (b.formData.order ?? 1)
                    )
                    .map(({ key, configKey, formData, value }) => ({
                        key,
                        title: formData.title || fromCamelCase(configKey),
                        description: formData.description,
                        value
                    }))
            }))
            .filter(({ entries: sectionEntries }) => sectionEntries.length);
    }

    getPlayerPositions(sportId) {
        return playerPositions[sportId];
    }

    getPlayerYears() {
        return Object.values(PlayerYears);
    }

    getRuleSetOptions(sportId) {
        return Object.keys(sportRuleSetMap[sportId]).map((id) => ({
            id,
            name: sportRuleSetMap[sportId][id].name
        }));
    }

    getDefaultRuleSetId(sportId) {
        return defaultRuleSetIdMap[sportId];
    }

    // Human-readable value for the change summary. Arrays of markers/areas
    // stringify to "[object Object]" by default, so format them explicitly.
    // When fmtPos is provided (marker/position fields), yard positions render
    // as H{yard}/V{yard}; field areas as "H0-H10 type", plain arrays joined.
    formatSummaryValue(value, fmtPos) {
        let result = value;
        if (typeof value === "boolean") {
            result = value ? "Yes" : "No";
        } else if (Array.isArray(value)) {
            result = value
                .map((v) => this.formatSummaryValue(v, fmtPos))
                .join(", ");
        } else if (value && typeof value === "object") {
            if (value.start !== undefined && value.end !== undefined) {
                const range = fmtPos
                    ? `${fmtPos(value.start)}-${fmtPos(value.end)}`
                    : `${value.start}-${value.end}`;
                // The config stores a code ("redZone"); FieldAreaType has
                // carried the readable name since it was written, and that is
                // what belongs on a sheet a crew reads.
                const typeName =
                    Object.values(FieldAreaType).find(
                        ({ code }) => code === value.type
                    )?.name || value.type;
                result = value.type ? `${range} ${typeName}` : range;
            } else {
                result = Object.values(value).join(" ");
            }
        } else if (fmtPos && typeof value === "number") {
            result = fmtPos(value);
        }
        return result;
    }

    // A yard position only means anything against a field length, so the
    // formatter is built per configuration rather than once.
    _positionFormatter(gameConfiguration) {
        const fieldLength = gameConfiguration?.general?.fieldLength;
        const converter = fieldLength ? new YardConverter(fieldLength) : null;

        return (pos) => {
            if (!converter || typeof pos !== "number") {
                return pos;
            }
            const { teamSide, yard } = converter.convertFromFieldPosition(pos);
            return `${teamSide}${yard}`;
        };
    }

    /**
     * The single place a stored value becomes something a crew can read, so
     * the definitions dialog, the printed sheet and the save confirmation
     * summary cannot drift apart (PSTATS-1267).
     *
     * An options field is shown by the option's NAME: what is stored is a code
     * - 1, "EL", true - and no crew has ever seen those on screen.
     */
    _formatSettingValue(value, formData, key, formatPosition) {
        let displayValue = this.formatSummaryValue(
            value,
            POSITIONAL_FIELDS.includes(key) ? formatPosition : undefined
        );

        if (formData?.options) {
            if (formData.multiselect) {
                displayValue = value
                    .map(
                        (code) =>
                            formData.options.find((o) => o.code === code)?.name
                    )
                    .join(", ");
            } else {
                displayValue = formData.options.find(
                    ({ code }) => code === value
                )?.name;
            }
        }

        return displayValue;
    }

    generateChangeSummary(sportId, ruleSetId, oldConfig, newConfig) {
        const customFormData = this.resolveCustomFormData(sportId, ruleSetId);
        const changes = {};
        const formatPosition = this._positionFormatter(newConfig);

        Object.keys(newConfig).forEach((key) => {
            Object.keys(newConfig[key]).forEach((childKey) => {
                const oldValue = oldConfig[key][childKey];
                const newValue = newConfig[key][childKey];

                if (!changes[key]) changes[key] = [];
                if (!areEqual(oldValue, newValue)) {
                    const formData = customFormData[key][childKey];
                    const title = formData?.title || fromCamelCase(childKey);
                    const displayValue = this._formatSettingValue(
                        newValue,
                        formData,
                        childKey,
                        formatPosition
                    );

                    changes[key].push({
                        setting: title,
                        message: `Has been ${
                            displayValue
                                ? `changed to '${displayValue}'`
                                : "cleared"
                        }`
                    });
                }
            });
        });

        return changes;
    }
}

export default new SportService();
